/**
 * Agent colour families — the four M² palette colours an agent's `avatar_color`
 * may name as a literal (`blue` / `green` / `red` / `yellow`), their canonical
 * hex values, and the deterministic name hash every avatar rule keys off.
 *
 * The texture IMAGES are no longer here. They are a brand asset —
 * `brand.agentAvatars` (types.ts), the M² preset ships them from
 * `/brand/m2/agent-avatars/` — and `brand/agent-avatars.ts` decides what an
 * agent wears. What stays is colour vocabulary shared by the chat tint, the
 * conversation colour cache, the sidebar and the Edit Agent picker.
 */

import { BRIJ_AGENT_MARK } from './agent-mark';

export type TextureColor = 'blue' | 'green' | 'red' | 'yellow';

/**
 * Canonical hex value for each colour family. Used by:
 *   • Edit Agent picker — a swatch of one of these hexes is stored as the
 *     TextureColor literal, any other as the raw hex.
 *   • `brand/agent-avatars.ts` — to read a literal as a colour.
 *
 * Match is case-insensitive on the 6-digit hex.
 */
export const TEXTURE_COLOR_HEX: Record<TextureColor, string> = {
  blue: '#6CC7EF',
  green: '#C4D600',
  red: '#D50032',
  yellow: '#FAB600',
};

/** Reverse lookup: hex → TextureColor (or null if not one of the four). */
export function hexToTextureColor(hex: string | null | undefined): TextureColor | null {
  if (!hex) return null;
  const normalized = hex.trim().toLowerCase();
  for (const [color, h] of Object.entries(TEXTURE_COLOR_HEX)) {
    if (h.toLowerCase() === normalized) return color as TextureColor;
  }
  return null;
}

/** Hue (degrees) and chroma (max − min channel, 0–1) of a `#RRGGBB`. */
export function hueChroma(hex: string): { h: number; c: number } {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [n >> 16, (n >> 8) & 255, n & 255].map(v => v / 255);
  const max = Math.max(r, g, b);
  const c = max - Math.min(r, g, b);
  let h = 0;
  if (c !== 0) {
    if (max === r) h = ((g - b) / c) % 6;
    else if (max === g) h = (b - r) / c + 2;
    else h = (r - g) / c + 4;
  }
  return { h: (h * 60 + 360) % 360, c };
}

export const hueGap = (a: number, b: number) => Math.min(Math.abs(a - b), 360 - Math.abs(a - b));

/**
 * The colour family of each Brij-mark colour (`agent-mark.ts`) — the nearest of
 * the four by hue. The Edit Agent picker of a brand without avatar sets stores a
 * mark colour's `base` hex, which no other part of the app knows: the chat tint,
 * the colour cache and the gallery read it through `resolveAgentTextureColor`,
 * the sidebar's recent-chat dot shows the base itself. Only these exact hexes;
 * any other hex keeps the rule it always had.
 */
export const MARK_BASE_FAMILY: ReadonlyMap<string, TextureColor> = new Map(
  BRIJ_AGENT_MARK.colors.map(({ base }) => {
    const { h } = hueChroma(base);
    const [family] = (Object.entries(TEXTURE_COLOR_HEX) as [TextureColor, string][])
      .map(([color, hex]) => [color, hueGap(hueChroma(hex).h, h)] as const)
      .sort((a, b) => a[1] - b[1])[0];
    return [base, family];
  }),
);

/** The family of a stored mark colour, or null when `raw` is not one. */
export function markBaseFamily(raw: string | null | undefined): TextureColor | null {
  return (raw && MARK_BASE_FAMILY.get(raw.trim().toUpperCase())) || null;
}

/** Is the given string one of the TextureColor literals? */
export function isTextureColor(value: string | null | undefined): value is TextureColor {
  return value === 'blue' || value === 'green' || value === 'red' || value === 'yellow';
}

/**
 * Normalize any value an agent's `avatar_color` field might hold into
 * a TextureColor literal that matches the `[data-agent-color="..."]`
 * CSS rules and the `coins[mode][color]` lookup. Falls back to the
 * deterministic name-hash if the input is empty or unrecognized.
 *
 * Without this, agents stored with a HEX `avatar_color` (e.g. the M²
 * 4-color palette) would push the raw hex into `data-agent-color` and
 * miss every CSS rule + every coin lookup — the chat-page tint would
 * silently fall back to a default that didn't match the avatar.
 */
export function resolveAgentTextureColor(
  raw: string | null | undefined,
  nameForFallback: string,
): TextureColor {
  if (isTextureColor(raw)) return raw;
  const mapped = hexToTextureColor(raw) ?? markBaseFamily(raw);
  if (mapped) return mapped;
  return getAgentColor(nameForFallback);
}

const COLORS: TextureColor[] = ['blue', 'green', 'red', 'yellow'];

/**
 * Simple deterministic hash from a string → number.
 * Same input always returns same output.
 */
export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Get just the color for an agent (for fallback solid backgrounds).
 */
export function getAgentColor(agentName: string): TextureColor {
  const hash = hashString(agentName || 'default');
  return COLORS[hash % COLORS.length];
}
